// #region Imports
import { createContext, useContext, useEffect, useReducer, useState } from 'react';
import {
    AdminKeyDropProcessHook,
    AdminLockerOperationContextState,
    AdminLockerOperationContextType,
    AdminLockerOperationDispatch,
    AdminLockerOperationDispatchContextType,
    AdminLockerOperationDispatchTypes,
    AdminLockerOperationProviderProps,
    AdminLockerOperationReducerFunction,
    AdminLockerOperationState,
    MzeroAdminKeyDropHook,
} from './adminLockerOperation.types';
import { GoMotoComponent, GoMotoFunction } from '../../API/GoMoto.types';
import css from './adminLockerOperation.module.scss';
import { KioskTypeName } from '../../API/Enums';
import { IAppointment } from '../../API/GoMoto';
import { GMButton } from '../../controls/Button';
import { GMDialog, GMDialogRetryCancel } from '../../controls/Dialog';
import { useAdminDispatch } from '../Admin/admin';
import { AdminDispatchState, AdminDispatchTypes } from '../Admin/admin.types';
import { useAppointmentStateUnsafe, useLockerOperation } from '../Appointment/appointment';
import { useKioskState } from '../Kiosk/kiosk';
import { useLocalization } from '../Localization/localization';
import { LocalizationType } from '../Localization/localization.types';
import { ILockerFixture } from '../lockers';
import { KeyDropError, KeyExchangeStage } from '../Appointment/appointment.types';
// #endregion

// #region Setup
/**
 * Defines the initial state object of the AdminLockerOperation context with all properties
 * set to either a falsy value or a custom initialization value.
 */
const initialState: AdminLockerOperationContextState = {
    lockerOpType: 'not-started',
    lockerId: undefined,
    returnPath: undefined,
    repullAppointment: false,
    isAdminCreate: false,
    keyStored: false,
};

/**
 * Sets up the AdminLockerOperation state context and defaults it to undefined so that if any
 * AdminLockerOperation context hook is used outside of the AdminLockerOperation state context, we can throw an error.
 */
const AdminLockerOperationStateContext: AdminLockerOperationContextType = createContext<AdminLockerOperationState>(undefined);

/**
 * Sets up the AdminLockerOperation dispatch context and defaults it to undefined so that if any
 * AdminLockerOperation hook that uses the dispatch context is used outside of the AdminLockerOperation dispatch context, we can throw an error.
 */
const AdminLockerOperationDispatchContext: AdminLockerOperationDispatchContextType = createContext<AdminLockerOperationDispatch>(undefined);
// #endregion

// #region Reducer
/**
 * Handles a single action dispatch to the AdminLockerOperation context to update
 * the internal state of the context.
 *
 * @param state - {@link AdminLockerOperationReducerFunction state} description.
 * @param action - {@link AdminLockerOperationReducerFunction action} description.
 */
export const adminLockerOperationReducer: AdminLockerOperationReducerFunction = (state, action) => {
    switch (action.type) {
        case AdminLockerOperationDispatchTypes.SetLockerOperation:
            return {
                ...state,
                ...action.payload,
            };
        case AdminLockerOperationDispatchTypes.SetReturnPath:
            return {
                ...state,
                returnPath: action.payload,
            };
        case AdminLockerOperationDispatchTypes.SetIsAdminCreate:
            return {
                ...state,
                isAdminCreate: action.payload,
            };
        case AdminLockerOperationDispatchTypes.SetRepullAppointment:
            return {
                ...state,
                repullAppointment: action.payload,
            };
        case AdminLockerOperationDispatchTypes.SetKeyStored:
            return {
                ...state,
                keyStored: action.payload,
            };
        case AdminLockerOperationDispatchTypes.Reset:
            /**
             * Preserve keyStored across Reset so the Loaded Keys tab can still pick it up
             * after the post-store-key navigation that resets the rest of the context.
             */
            return {
                ...initialState,
                keyStored: state.keyStored,
            };
        default:
            return state;
    }
};
// #endregion

// #region Provider
/**
 * Provides the AdminLockerOperation state and a dispatch to update the state to all components that
 * are rendered as a child node of this component.
 *
 * @param children - {@link AdminLockerOperationProviderProps children} description.
 */
export const AdminLockerOperationProvider: GoMotoComponent<AdminLockerOperationProviderProps> = ({
    children,
}) => {
    const [state, dispatch] = useReducer<AdminLockerOperationReducerFunction>(adminLockerOperationReducer, initialState);

    return (
        <AdminLockerOperationStateContext.Provider value={state}>
            <AdminLockerOperationDispatchContext.Provider value={dispatch}>
                {children}
            </AdminLockerOperationDispatchContext.Provider>
        </AdminLockerOperationStateContext.Provider>
    );
};
// #endregion

// #region State Hook
/**
 * AdminLockerOperation context hook that retrieves the state from the context, or throws
 * an error if not used in a component that is a child of the AdminLockerOperation context.
 */
export const useAdminLockerOperationState = () => {
    const context: AdminLockerOperationState = useContext(AdminLockerOperationStateContext);

    if (context === undefined) {
        throw new Error('useAdminLockerOperationState must be used within an AdminLockerOperationProvider');
    }

    return context;
};
// #endregion

// #region Dispatch Hook
/**
 * AdminLockerOperation context hook that returns a dispatch function which can dispatch actions to
 * update the state of the AdminLockerOperation context, or throws an error if not used in a
 * component that is a child of the AdminLockerOperation context.
 */
export const useAdminLockerOperationDispatch = () => {
    const context: AdminLockerOperationDispatch = useContext(AdminLockerOperationDispatchContext);

    if (context === undefined) {
        throw new Error('useAdminLockerOperationDispatch must be used within an AdminLockerOperationProvider');
    }

    return context;
};
// #endregion

// #region AdminKeyDrop
/**
 * This hook manages the dialogs and locker operations being performed for The 24/7 kiosk.
 *
 * @note This is a separate hook so that it is easier to consolidate restricted locker operation logic
 * that is specific to MZero kiosks. It is also intended to make it easier to remove the MZero specific
 * logic when we end support for the MZero kiosks.
 *
 * For information regarding the return type or hook type definitions, click {@link MzeroAdminKeyDropHook here}.
 */
export const useMzeroAdminKeyDrop: MzeroAdminKeyDropHook = () => {
    const Localization: LocalizationType = useLocalization();
    const { keyStorageType, kioskTypeName } = useKioskState();
    const adminDispatch: AdminDispatchState = useAdminDispatch();
    const adminLockerOperationDispatch: AdminLockerOperationDispatch = useAdminLockerOperationDispatch();
    const { error: keyRetrieveError, start: retrieveKey, inProgress: keyRetrieveInProgress, locker: keyRetrieveLocker, complete } = useLockerOperation({ byAdvisor: true });

    const [error, setError] = useState<string | undefined>(undefined);

    const isThe247: boolean = kioskTypeName === KioskTypeName.The247;

    const onRetryOpenLocker: GoMotoFunction<[void], Promise<void>> = async () => {
        setError(undefined);

        await retrieveKey({
            lockerAction: 'retrieve-key',
            manual: true,
        })
            .catch(setError);

        adminLockerOperationDispatch({ type: AdminLockerOperationDispatchTypes.Reset });
    };

    const onRetrieveKeyClick: GoMotoFunction<
        [IAppointment | undefined],
        Promise<void>
    > = async (appt: IAppointment | undefined = undefined, locker: ILockerFixture | undefined = undefined) => {
        return new Promise<void>(async (resolve) => {
            await retrieveKey({
                lockerAction: 'retrieve-key',
                manual: true,
                appt: appt,
            });

            adminLockerOperationDispatch({ type: AdminLockerOperationDispatchTypes.Reset });

            return resolve();
        });
    };

    const MzeroAdminKeyDrop: GoMotoComponent = () => {
        if (kioskTypeName !== KioskTypeName.The247) {
            return null;
        }
        else {
            return (
                <>
                    <GMDialog
                        ariaLabel={Localization.RetrieveKey}
                        title={Localization.RetrieveKey}
                        open={keyRetrieveInProgress && !!keyRetrieveLocker}
                        hideBackdrop
                    >
                        {keyStorageType === 'locker' && keyRetrieveLocker
                            ? Localization.formatString(Localization.RetrieveYourKeyFromLocker, keyRetrieveLocker.Id)
                            : Localization.RetrieveYourKey}
                    </GMDialog>
                    <GMDialogRetryCancel
                        ariaLabel={Localization.Retry}
                        open={!!error}
                        onCancel={() => setError(undefined)}
                        onRetry={onRetryOpenLocker}
                    >
                        {error}
                    </GMDialogRetryCancel>
                </>
            );
        }
    };

    useEffect(() => {
        if (isThe247) {
            if (complete && keyRetrieveLocker) {
                adminDispatch({
                    type: AdminDispatchTypes.SetLockerChanged,
                    payload: keyRetrieveLocker,
                });
            }
        }
    }, [complete, keyRetrieveLocker, adminDispatch, kioskTypeName, isThe247]);

    useEffect(() => {
        if (isThe247) {
            if (keyRetrieveError !== KeyDropError.none && isThe247) {
                setError(Localization.FailedToOpenLocker);
            }
        }
    }, [keyRetrieveError, Localization.FailedToOpenLocker, kioskTypeName, isThe247]);

    return {
        onMzeroRetrieveKeyClick: isThe247 ? onRetrieveKeyClick : null,
        MzeroAdminKeyDrop: MzeroAdminKeyDrop,
    };
};

/**
 * This hook manages the dialogs that need to display for non-24/7 kiosks, while attempting to
 * open the locker and retrieve keys from the admin screen. This also includes the MZero specific logic
 * when the kiosk is an MZero kiosk.
 *
 * For information regarding the return type or hook type definitions, click {@link AdminKeyDropProcessHook here}.
 */
export const useAdminKeyDropProcess: AdminKeyDropProcessHook = ({
    onProceed,
    setMzeroRetrieveComplete: setMzeroComplete,
}) => {
    const Localization: LocalizationType = useLocalization();
    const { kioskTypeName } = useKioskState();
    const { MzeroAdminKeyDrop, onMzeroRetrieveKeyClick } = useMzeroAdminKeyDrop();
    const appointment: IAppointment | undefined = useAppointmentStateUnsafe();
    const adminLockerOperationDispatch: AdminLockerOperationDispatch = useAdminLockerOperationDispatch();

    const [isOpenLockerDialogOpen, setIsOpenLockerDialogOpen] = useState<boolean>(false);
    const [isReturnKeyDialogOpen, setIsReturnKeyDialogOpen] = useState<boolean>(false);
    const [lockerId, setLockerId] = useState<ILockerFixture['Id'] | undefined>(undefined);
    const [showPaymentRequired, setShowPaymentRequired] = useState<boolean>(false);

    const isExpressOrThe365: boolean = kioskTypeName === KioskTypeName.TheExpress || kioskTypeName === KioskTypeName.The365;

    const resetKeyDropStates: VoidFunction = () => {
        if (lockerId !== undefined) {
            setLockerId(undefined);
        }

        adminLockerOperationDispatch({ type: AdminLockerOperationDispatchTypes.Reset });
    };

    const showKeyDropProcessDialog: ReturnType<AdminKeyDropProcessHook>['showKeyDropProcessDialog'] = (showDialog) => {
        switch (showDialog) {
            case 'payment-required':
                setShowPaymentRequired(true);
                break;
            case 'open-locker':
                setIsOpenLockerDialogOpen(true);
                break;
            case 'return-key':
                setIsReturnKeyDialogOpen(true);
                break;
            default:
                break;
        }
    };

    const AdminKeyDropProcess: ReturnType<AdminKeyDropProcessHook>['AdminKeyDropProcess'] = () => {
        return (
            <>
                <GMDialog
                    ariaLabel={Localization.AdjustContentsOfLocker}
                    open={isOpenLockerDialogOpen}
                    paperDialogClasses={css.dialog}
                    className={css.dialogTitle}
                    rootDialogContentClasses={css.dialogContent}
                    title={Localization.AdjustContentsOfLocker}
                    actions={[
                        (
                            <GMButton
                                id={Localization.GoBack}
                                title={Localization.GoBack}
                                ariaLabel={Localization.GoBack}
                                styleType={'secondary'}
                                onClick={() => {
                                    resetKeyDropStates();

                                    setIsOpenLockerDialogOpen(false);
                                }}
                            >
                                {Localization.GoBack}
                            </GMButton>
                        ),
                        (
                            <GMButton
                                id={Localization.OpenLocker}
                                title={Localization.OpenLocker}
                                ariaLabel={Localization.OpenLocker}
                                styleType={'primary'}
                                onClick={() => {
                                    if (appointment !== undefined) {
                                        adminLockerOperationDispatch({
                                            type: AdminLockerOperationDispatchTypes.SetLockerOperation,
                                            payload: {
                                                lockerOpType: 'open-locker',
                                            },
                                        });

                                        onProceed();
                                    }
                                    else if (lockerId !== undefined) {
                                        adminLockerOperationDispatch({
                                            type: AdminLockerOperationDispatchTypes.SetLockerOperation,
                                            payload: {
                                                lockerOpType: 'open-locker',
                                                lockerId: lockerId,
                                            },
                                        });

                                        onProceed();
                                    }
                                    else {
                                        resetKeyDropStates();

                                        setIsOpenLockerDialogOpen(false);
                                    }
                                }}
                            >
                                {Localization.OpenLocker}
                            </GMButton>
                        ),
                    ]}
                >
                    {Localization.ThisActionIsForViewingOrAdjustingLockerContents}
                </GMDialog>
                <GMDialog
                    ariaLabel={Localization.RetrieveKeyFromLocker}
                    open={isReturnKeyDialogOpen}
                    paperDialogClasses={css.dialog}
                    className={css.dialogTitle}
                    rootDialogContentClasses={css.dialogContent}
                    title={Localization.RetrieveKeyFromLocker}
                    actions={[
                        (
                            <GMButton
                                id={Localization.GoBack}
                                title={Localization.GoBack}
                                ariaLabel={Localization.GoBack}
                                styleType={'secondary'}
                                onClick={() => {
                                    resetKeyDropStates();

                                    setIsReturnKeyDialogOpen(false);
                                }}
                            >
                                {Localization.GoBack}
                            </GMButton>
                        ),
                        (
                            <GMButton
                                id={Localization.RetrieveKey}
                                title={Localization.RetrieveKey}
                                ariaLabel={Localization.RetrieveKey}
                                styleType={'primary'}
                                onClick={() => {
                                    if (appointment) {
                                        const canRetrieveExchangedKey: boolean = isExpressOrThe365 && appointment.keyExchangeStage === KeyExchangeStage.AdminKeyRetrieve;

                                        adminLockerOperationDispatch({
                                            type: AdminLockerOperationDispatchTypes.SetLockerOperation,
                                            payload: {
                                                lockerOpType: canRetrieveExchangedKey ? 'retrieve-exchanged-key' : 'retrieve-key',
                                            },
                                        });

                                        onProceed();
                                    }
                                    else {
                                        resetKeyDropStates();

                                        setIsReturnKeyDialogOpen(false);
                                    }
                                }}
                            >
                                {Localization.RetrieveKey}
                            </GMButton>
                        ),
                    ]}
                >
                    {Localization.ThisActionWillResetTheKeyReturnProcess}
                </GMDialog>
                <GMDialog
                    ariaLabel={Localization.WarningAPaymentIsDue}
                    open={showPaymentRequired}
                    paperDialogClasses={css.dialog}
                    className={css.dialogTitle}
                    rootDialogContentClasses={css.dialogContent}
                    title={Localization.MarkAsPaid}
                    actions={[
                        <GMButton
                            id={Localization.Cancel}
                            title={Localization.Cancel}
                            ariaLabel={Localization.Cancel}
                            styleType={'secondary'}
                            onClick={() => {
                                resetKeyDropStates();

                                setShowPaymentRequired(false);
                            }}
                        >
                            {Localization.Cancel}
                        </GMButton>,
                        <GMButton
                            id={Localization.MarkAsPaid}
                            title={Localization.MarkAsPaid}
                            ariaLabel={Localization.MarkAsPaid}
                            styleType={'primary'}
                            onClick={async () => {
                                setShowPaymentRequired(false);

                                if (kioskTypeName !== KioskTypeName.The247) {
                                    setIsReturnKeyDialogOpen(true);
                                }
                                else {
                                    onMzeroRetrieveKeyClick && await onMzeroRetrieveKeyClick(appointment);
                                    setMzeroComplete && setMzeroComplete(true);
                                }
                            }}
                        >
                            {Localization.MarkAsPaid}
                        </GMButton>,
                    ]}
                >
                    {Localization.WarningAPaymentIsDue}
                </GMDialog>
                <MzeroAdminKeyDrop />
            </>
        );
    };

    return {
        AdminKeyDropProcess: AdminKeyDropProcess,
        setLockerId: setLockerId,
        showKeyDropProcessDialog,
        onMzeroRetrieveKeyClick: onMzeroRetrieveKeyClick,
    };
};
// #endregion